import React, { type ComponentType, useState } from 'react';

import { type GlyphProps, type NewIconProps } from '@atlaskit/icon';
import CopyIcon from '@atlaskit/icon/core/copy';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Pressable, xcss } from '@atlaskit/primitives';

import CopyToClipboard from '../../copy-to-clipboard';

const copyIconstyles = xcss({ flexGrow: 1, textAlign: 'right' });

type CodeIconCopyButtonProps = {
	name: string;
	suffixElement?: React.ReactNode;
	icon?: ComponentType<Omit<NewIconProps, 'spacing'> & { spacing?: 'spacious' }>;
	legacyIcon?: ComponentType<GlyphProps>;
	onClick: () => void;
};

const buttonStyles = xcss({
	textAlign: 'left',
	width: '100%',
	paddingBlock: 'space.075',
	borderRadius: 'radius.xsmall',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral',
	},
	color: 'color.text',
});

const iconNameStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.xsmall',
	font: 'font.code',
	padding: 'space.050',
});

const iconNameHoverStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
});

const CodeIconCopyButton = ({
	name,
	suffixElement,
	icon: Icon,
	legacyIcon: LegacyIcon,
	onClick,
}: CodeIconCopyButtonProps) => {
	const [isMouseOver, setIsMouseOver] = useState(false);

	const mouseEnterHandler = () => {
		setIsMouseOver(true);
	};

	const mouseLeaveHandler = () => {
		setIsMouseOver(false);
	};

	return (
		<Pressable
			xcss={buttonStyles}
			onMouseEnter={mouseEnterHandler}
			onMouseLeave={mouseLeaveHandler}
			onClick={() => {
				onClick();
			}}
		>
			<Inline space="space.100" alignBlock="center">
				{Icon ? <Icon label="" spacing="spacious" /> : LegacyIcon && <LegacyIcon label="" />}
				<Box xcss={[iconNameStyles, isMouseOver && iconNameHoverStyles]}>{name}</Box>
				{suffixElement}
				<Box xcss={copyIconstyles}>
					<CopyIcon spacing="spacious" label="" color="currentColor" />
				</Box>
			</Inline>
		</Pressable>
	);
};

type CodeIconCopyToClipboardProps = {
	value: string;
	name: string;
	suffixElement?: React.ReactNode;
	icon?: ComponentType<Omit<NewIconProps, 'spacing'> & { spacing?: 'spacious' }>;
	legacyIcon?: ComponentType<GlyphProps>;
	prompt: string;
};

/**
 * __Code icon copy to clipboard__
 *
 * A component that displays an icon, name and a statement which can be copied to the clipboard when clicked.
 */
const CodeIconCopyToClipboard = ({
	value,
	prompt,
	...props
}: CodeIconCopyToClipboardProps): React.JSX.Element => {
	return (
		<CopyToClipboard
			value={value}
			messages={{
				prompt: prompt,
				success: `Copied "${value}"`,
				fail: 'Copy failed',
			}}
			resetMs={2000}
		>
			{({ copy }) => <CodeIconCopyButton onClick={copy} {...props} />}
		</CopyToClipboard>
	);
};

export default CodeIconCopyToClipboard;
